import { type ComponentType } from "react";

import { BdsIconPlans, BdsIconStamp } from "@buildertrend/components";

import {
    BTIconBids,
    BTIconBillsPurchaseOrders,
    BTIconCamera,
    BTIconChangeOrders,
    BTIconCostItemLinked,
    BTIconCreditMemos,
    BTIconDailyLogs,
    BTIconDocuments,
    BTIconJobs,
    BTIconLeadOpportunities,
    BTIconListChecks,
    BTIconOwnerInvoices,
    BTIconPurchaseOrderPayments,
    BTIconPurchaseOrders,
    BTIconReceipts,
    BTIconRFIs,
    BTIconSchedules,
    BTIconSelections,
    BTIconSpecifications,
    BTIconTodos,
    BTIconVideos,
    BTIconWarranty,
    type IBTIconProps,
} from "commonComponents/btWrappers/BTIcon";
import {
    type IRelatedItemEntityData,
    RelatedItemType,
} from "commonComponents/entity/relatedItem/RelatedItem.types";

function getGenericRelatedItemTagIcon(iconPath: string): ComponentType<IBTIconProps> {
    return (iconProps) => <img src={iconPath} alt={iconProps.title} {...iconProps} />;
}

export function getRelatedItemEntityData(type: RelatedItemType): IRelatedItemEntityData {
    switch (type) {
        case RelatedItemType.ChangeOrder:
            return {
                name: "Change Order",
                namePlural: "Change Orders",
                RelatedIcon: BTIconChangeOrders,
            };
        case RelatedItemType.ToDo:
            return {
                name: "To-Do",
                namePlural: "To-Dos",
                RelatedIcon: BTIconTodos,
            };
        case RelatedItemType.PurchaseOrder:
            return {
                name: "Purchase Order",
                namePlural: "Purchase Orders",
                RelatedIcon: BTIconPurchaseOrders,
            };
        case RelatedItemType.CustomerInvoice:
            return {
                name: "Invoice",
                namePlural: "Invoices",
                RelatedIcon: BTIconOwnerInvoices,
            };
        case RelatedItemType.Warranty:
            return {
                name: "Warranty",
                namePlural: "Warranties",
                RelatedIcon: BTIconWarranty,
            };
        case RelatedItemType.ScheduleItem:
            return {
                name: "Schedule Item",
                namePlural: "Schedule Items",
                RelatedIcon: BTIconSchedules,
            };
        case RelatedItemType.BidPackage:
            return {
                name: "Bid Package",
                namePlural: "Bid Packages",
                RelatedIcon: BTIconBids,
            };
        case RelatedItemType.PhotoDoc:
            return {
                name: "Photo",
                namePlural: "Photos",
                RelatedIcon: BTIconCamera,
            };
        case RelatedItemType.Video:
            return {
                name: "Video",
                namePlural: "Videos",
                RelatedIcon: BTIconVideos,
            };
        case RelatedItemType.Document:
            return {
                name: "Document",
                namePlural: "Documents",
                RelatedIcon: BTIconDocuments,
            };
        case RelatedItemType.Allowance:
            return {
                name: "Allowance",
                namePlural: "Allowances",
                RelatedIcon: BTIconSelections,
            };
        case RelatedItemType.SelectionChoice:
            return {
                name: "Selection Choice",
                namePlural: "Selection Choices",
                RelatedIcon: BTIconSelections,
            };
        case RelatedItemType.Bid:
            return {
                name: "Bid",
                namePlural: "Bids",
                RelatedIcon: getGenericRelatedItemTagIcon("/images/calendar/bidsIcon.png"),
            };
        case RelatedItemType.DailyLog:
            return {
                name: "Daily Log",
                namePlural: "Daily Logs",
                RelatedIcon: BTIconDailyLogs,
            };
        case RelatedItemType.Rfi:
            return {
                name: "RFI",
                namePlural: "RFIs",
                RelatedIcon: BTIconRFIs,
            };
        case RelatedItemType.PurchaseOrderPaymentLienWaiver:
        case RelatedItemType.BillLienWaiver:
            return {
                name: "Lien Waiver",
                namePlural: "Lien Waivers",
                RelatedIcon: getGenericRelatedItemTagIcon("/images/calendar/toDoIcon.png"),
            };
        case RelatedItemType.Jobsite:
            return {
                name: "Job",
                namePlural: "Jobs",
                RelatedIcon: BTIconJobs,
            };
        case RelatedItemType.Selection:
            return {
                name: "Selection",
                namePlural: "Selections",
                RelatedIcon: BTIconSelections,
            };
        case RelatedItemType.Specification:
            return {
                name: "Specification",
                namePlural: "Specifications",
                RelatedIcon: BTIconSpecifications,
            };
        case RelatedItemType.Lead:
            return {
                name: "Lead",
                namePlural: "Leads",
                RelatedIcon: BTIconLeadOpportunities,
            };
        case RelatedItemType.LeadProposal:
            return {
                name: "Lead Proposal",
                namePlural: "Lead Proposals",
                RelatedIcon: BTIconLeadOpportunities,
            };
        case RelatedItemType.CreditMemo:
            return {
                name: "Credit Memo",
                namePlural: "Credit Memos",
                RelatedIcon: BTIconCreditMemos,
            };
        case RelatedItemType.JobProposal:
        case RelatedItemType.Estimate:
            return {
                name: "Estimate",
                namePlural: "Estimates",
                RelatedIcon: getGenericRelatedItemTagIcon("/images/calendar/estimatesIcon.svg"),
            };
        case RelatedItemType.CostGroup:
            return {
                name: "Cost Group",
                namePlural: "Cost Groups",
                RelatedIcon: getGenericRelatedItemTagIcon("/images/calendar/costGroupsIcon.svg"),
            };
        case RelatedItemType.CostItem:
            return {
                name: "Catalog",
                namePlural: "Catalog",
                RelatedIcon: BTIconCostItemLinked,
            };
        case RelatedItemType.UnsentMessage:
        case RelatedItemType.SentMessage:
            return {
                name: "Message",
                namePlural: "Messages",
                RelatedIcon: getGenericRelatedItemTagIcon("/images/calendar/messagesIcon.svg"),
            };
        case RelatedItemType.Bill:
            return {
                name: "Bill",
                namePlural: "Bills",
                RelatedIcon: BTIconBillsPurchaseOrders,
            };
        case RelatedItemType.PurchaseOrderPayment:
            return {
                name: "Purchase Order Payment",
                namePlural: "Purchase Order Payments",
                RelatedIcon: getGenericRelatedItemTagIcon("/images/calendar/poPaymentsIcon.svg"),
            };
        case RelatedItemType.Takeoff:
            return {
                name: "Takeoff",
                namePlural: "Takeoffs",
                RelatedIcon: getGenericRelatedItemTagIcon("/images/calendar/estimatesIcon.svg"),
            };
        case RelatedItemType.Rebate:
            return {
                name: "Rebate",
                namePlural: "Rebates",
                RelatedIcon: getGenericRelatedItemTagIcon("/images/calendar/rebatesIcon.svg"),
            };
        case RelatedItemType.Deposit:
            return {
                name: "Deposit",
                namePlural: "Deposits",
                RelatedIcon: BTIconPurchaseOrderPayments,
            };
        case RelatedItemType.OnlinePayment:
            return {
                name: "Online Payment",
                namePlural: "Online Payments",
                RelatedIcon: BTIconPurchaseOrderPayments,
            };
        case RelatedItemType.SelectionOption:
            return {
                name: "Selection Option",
                namePlural: "Selection Options",
                RelatedIcon: BTIconSelections,
            };
        case RelatedItemType.None:
            return {
                name: "",
                namePlural: "",
                RelatedIcon: getGenericRelatedItemTagIcon(""),
            };
        case RelatedItemType.ClientUpdate:
            return {
                name: "Client Update",
                namePlural: "Client Updates",
                RelatedIcon: getGenericRelatedItemTagIcon(""),
            };
        case RelatedItemType.Receipt:
            return {
                name: "Receipt",
                namePlural: "Receipts",
                RelatedIcon: BTIconReceipts,
            };
        case RelatedItemType.Task:
            return {
                name: "Task",
                namePlural: "Tasks",
                RelatedIcon: BTIconListChecks,
            };
        case RelatedItemType.PlanSet:
            return {
                name: "Plan Set",
                namePlural: "Plan Sets",
                RelatedIcon: () => <BdsIconPlans size="sm" />,
            };
        case RelatedItemType.Submittal:
            return {
                name: "Submittal",
                namePlural: "Submittals",
                RelatedIcon: () => <BdsIconStamp size="sm" />,
            };
        case RelatedItemType.AllowanceOption:
            return {
                name: "Selection options",
                namePlural: "Selection options",
                RelatedIcon: BTIconSelections,
            };
        default:
            const test: never = type; // Should throw compiler error
            throw new Error(`Unknown related item type:  ${test as unknown}`);
    }
}
